import { CheckCircle2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Card } from '@/components/public/Section';
import { Button } from '@/components/ui/button';
import { Field, FormError, TextareaField } from '@/components/ui/field';
import { useBlurValidation } from '@/hooks/use-blur-validation';
import { api } from '@/lib/api';
import { NAP } from '@/lib/public-content';
import { HONEYPOT_FIELD, contactMessageSchema } from '@shared/schemas/intake';

/*
 * The short contact form — ROADMAP.md §8.6: "Contact and quote forms both
 * create a `lead` with the right `source`."
 *
 * **Only the quote form exists on the live site**, so this half is new. It is
 * for somebody with a question rather than a property to price, and it writes
 * `source = 'contact_form'`, which is what makes the two tellable apart in the
 * leads inbox. Kept deliberately short: a four-field form beside a twenty-field
 * one is only worth having if it is obviously the quicker path.
 */

export function ContactForm() {
  const [renderedAt] = useState(() => Date.now());

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const values = useMemo(
    () => ({
      name,
      email,
      phone: phone.trim() === '' ? undefined : phone,
      message,
      [HONEYPOT_FIELD]: honeypot,
      renderedAt,
    }),
    [name, email, phone, message, honeypot, renderedAt],
  );

  const { errors, isValid, parsed, touch, touchAll, reset } = useBlurValidation(
    contactMessageSchema,
    values,
  );

  function submit(event: React.FormEvent) {
    event.preventDefault();
    setFailure(null);
    touchAll();
    if (!isValid || parsed === null) return;

    setSubmitting(true);
    api
      .post('/api/public/intake/contact', parsed)
      .then(() => {
        setSubmitted(true);
        reset();
      })
      .catch(() => {
        setFailure(
          `Something went wrong sending that. Please try again, or ring us on ${NAP.phone}.`,
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  }

  if (submitted) {
    return (
      <Card>
        <div className="flex gap-3">
          <CheckCircle2 className="mt-0.5 size-6 shrink-0 text-status-completed-text" aria-hidden />
          <div>
            <h2 aria-live="polite" className="text-h2">
              Message sent
            </h2>
            <p className="mt-2 text-body text-ink-700">
              Thanks — we will come back to you shortly.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="text-h2">Just have a question?</h2>
      <p className="mt-2 text-body text-ink-700">
        Send us a note and we will get back to you. No property details needed.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-5" noValidate>
        <FormError>{failure}</FormError>

        {/* Hidden from sight and from assistive technology — see QuoteForm. */}
        <div aria-hidden="true" className="hidden">
          <label htmlFor="contact-company-website">Company website</label>
          <input
            id="contact-company-website"
            name={HONEYPOT_FIELD}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(event) => {
              setHoneypot(event.target.value);
            }}
          />
        </div>

        <Field
          label="Name"
          name="name"
          required
          autoComplete="name"
          placeholder="Your name"
          value={name}
          error={errors.name}
          onChange={(event) => {
            setName(event.target.value);
          }}
          onBlur={() => {
            touch('name');
          }}
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <Field
            label="Email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="you@email.com"
            value={email}
            error={errors.email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            onBlur={() => {
              touch('email');
            }}
          />
          <Field
            label="Phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            placeholder="(435) 000-0000"
            hint="Optional"
            value={phone}
            error={errors.phone}
            onChange={(event) => {
              setPhone(event.target.value);
            }}
            onBlur={() => {
              touch('phone');
            }}
          />
        </div>

        <TextareaField
          label="Message"
          name="message"
          required
          rows={5}
          placeholder="What can we help with?"
          value={message}
          error={errors.message}
          onChange={(event) => {
            setMessage(event.target.value);
          }}
          onBlur={() => {
            touch('message');
          }}
        />

        <Button type="submit" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send message'}
        </Button>
      </form>
    </Card>
  );
}
